import React, { useState, useEffect, useContext, useMemo } from "react";
import { ModalWrapper } from "../../../../components/ui";

import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";

import handleSendMessages from "../../../../utils/api/messaging";
import { updateDispatchStage } from "../../../../services/api/dispatch";
import { captureDispatch } from "../../../../utils/business";
import { Button } from "../../../../components/ui";
import { SearchableDropdown } from "../../../../components/ui";
import { TextMessagePreview } from "../../../../components/ui";
import SMSCostWarning from "./SMSCostWarning";

import { X } from "../../../../components/icons/svg/outline";

import { compareWorkersByName, getWorkerSMSMessages } from "../../../../utils/business";
import { formatDate, getDateString } from "../../../../utils/datetime";
import ReviewResponseModal from "./ReviewResponseModal";

const TITLE_ID = "dispatch-sms-modal-title";

/**
 * DispatchModal component for displaying and sending SMS messages to selected workers.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Date} props.date - The date for which the SMS messages are being sent.
 * @param {Object} props.selectedWorkers - Workers to whom the SMS messages are sent.
 * @param {boolean} props.isOpen - Flag indicating whether the modal is open.
 * @param {function} props.onClose - Function to call when modal is closed (X button).
 * @returns {JSX.Element|null} The rendered component or null if the modal is not open.
 */
const DispatchModal = ({ date, selectedWorkers, isOpen, onClose }) => {
  const messages = getWorkerSMSMessages(selectedWorkers);

  const [selectedWorker, setSelectedWorker] = useState("");
  const [messageText, setMessageText] = useState("");
  const [response, setResponse] = useState(null); // Change state to store the entire response
  const [isSending, setIsSending] = useState(false);
  const [isResponseOpen, setIsResponseOpen] = useState(false);
  const { weekStart } = useContext(WeeklyContext);
  const {
    dayDetails,
    setIsDispatchCapturing,
    reloadDays,
    setIsReadOnly,
    runPlanningOperation,
  } =
    useContext(DispatchContext);

  useEffect(() => {
    if (isOpen) {
      setIsSending(false);
    }
  }, [isOpen]);

  const previewWorkerOptions = useMemo(
    () =>
      [...messages]
        .sort((a, b) => compareWorkersByName(a.worker, b.worker))
        .map((message) => ({
          value: message.worker.id,
          label: `${message.worker.first} ${message.worker.last}`.trim(),
        })),
    [messages]
  );

  if (!isOpen) return null;

  /**
   * Load the SMS preview text for the worker chosen in the dropdown.
   *
   * @param {string|number|null} workerId Selected worker id.
   * @returns {void}
   */
  const handlePreviewWorkerChange = (workerId) => {
    setSelectedWorker(workerId);
    const selectedMessage = messages.find(
      (message) => String(message.worker.id) === String(workerId)
    );
    setMessageText(selectedMessage ? selectedMessage.message : "");
  };

  // Send all messages to the selected workers, handle the response from the API, and
  // display the response message in a new modal (ReviewResponseModal - bottom of comp)
  /**
   * Send schedule SMS, capture the Dispatch page, and mark the day Dispatched.
   *
   * @param {void}
   * @returns {Promise<void>}
   */
  const handleSendMessagesClick = async () => {
    setIsSending(true);
    try {
      const response = await handleSendMessages(messages);
      setResponse(response); // Store the entire response
      setIsResponseOpen(true);
    } catch (error) {
      setResponse({
        error:
          error.response?.data?.error || "Some SMS messages failed to send",
        errors: error.response?.data?.errors || [],
        success: error.response?.data?.success || [],
      });
    }
    try {
      setIsDispatchCapturing(true);
      setIsReadOnly(false);
      captureDispatch(setIsDispatchCapturing, weekStart, date);
    } catch (error) {
      console.error(error);
    }
    try {
      await runPlanningOperation({
        module: "dispatch",
        operation: "markDayDispatched",
        resources: ["dispatch.dispatch_stage"],
        apiCall: () => updateDispatchStage("Dispatched", date),
      });
      await reloadDays();
    } catch (error) {
      console.error(error);
    }
  };

  // Close the modal and reset the states
  /**
   * Close the modal and reset preview/response state.
   *
   * @param {void}
   * @returns {void}
   */
  const handleClose = () => {
    onClose();
    setResponse(null);
    setIsResponseOpen(false);
    setMessageText("");
    setSelectedWorker("");
  };

  const dispatchDetails = dayDetails.filter(
    (detail) => detail.date === getDateString(date)
  )[0];
  const dispatchStages = dispatchDetails["dispatch_stage"];

  var alreadyDispatched = [];
  if (dispatchStages) {
    alreadyDispatched = dispatchStages.filter(
      (detail) => detail.stage === "Dispatched"
    );
  }

  return (
    <>
      {selectedWorkers && dayDetails && (
        <ModalWrapper
          isOpen={isOpen}
          onClose={handleClose}
          closeButton={false}
          maxWidth={800}
          className="relative w-full max-h-[90vh] overflow-y-auto rounded-xl bg-white p-0 shadow-xl"
          outsideClickIgnoreSelector="[data-searchable-dropdown-listbox]"
        >
          <div role="dialog" aria-modal="true" aria-labelledby={TITLE_ID}>
            <div className="flex items-center justify-between gap-3 bg-primary px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <h2
                  id={TITLE_ID}
                  className="text-lg font-semibold leading-none tracking-tight text-white"
                >
                  SMS Schedule
                </h2>
                <p className="text-sm leading-none text-white/80">
                  {formatDate(date, "weekday MM/DD/YY")}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded text-primary-100 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
                aria-label="Close"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <div className="space-y-2 px-5 py-4 pt-2">
              {alreadyDispatched.length > 0 && !isResponseOpen ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                  <p className="font-semibold">
                    SMS messages have already been sent for this day
                  </p>
                  <ul className="mt-1.5 list-disc space-y-0.5 pl-5">
                    {alreadyDispatched.map((detail, index) => {
                      const time = detail.time.split(":").slice(0, 2).join(":");
                      return (
                        <li key={index}>
                          {detail.date}, {time} ({detail.user.first}{" "}
                          {detail.user.last})
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
              {dayDetails.sms_author && dayDetails.sms_time ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                  SMS scheduling messages have already been sent for this day by{" "}
                  <span className="font-semibold">
                    {dayDetails.sms_author.first} {dayDetails.sms_author.last}
                  </span>{" "}
                  at{" "}
                  <span className="font-semibold">
                    {dayDetails.sms_time.slice(0, 5)}
                  </span>
                  .
                </div>
              ) : null}

              <section>
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Recipients
                  </h3>
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                    {selectedWorkers.allWorkers.length}{" "}
                    {selectedWorkers.allWorkers.length === 1
                      ? "recipient"
                      : "recipients"}
                  </span>
                </div>
                <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-2">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {[...selectedWorkers.allWorkers]
                      .sort(compareWorkersByName)
                      .map((worker, index) => (
                        <p
                          key={`sms-worker-${index}`}
                          className="text-sm text-gray-800"
                        >
                          {worker.first} {worker.last}
                        </p>
                      ))}
                  </div>
                </div>
              </section>

              <section>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-800">
                    Message
                  </h3>
                  <SearchableDropdown
                    options={previewWorkerOptions}
                    value={selectedWorker || null}
                    onChange={handlePreviewWorkerChange}
                    placeholder="Select a worker to preview..."
                    ariaLabel="Preview worker message"
                    searchWhenOverflow
                    className="w-56"
                    listboxClassName="z-[10060]"
                  />
                </div>
                {messageText ? (
                  <div className="rounded-lg bg-gray-50 px-3 py-3">
                    <TextMessagePreview
                      text={messageText}
                      label="Sample Message Preview"
                    />
                  </div>
                ) : (
                  <p className="rounded-lg border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-400">
                    Select a worker to preview the message they will receive.
                  </p>
                )}
              </section>

              <SMSCostWarning
                messageCount={selectedWorkers.allWorkers.length}
              />
              <Button
                variant={isSending ? "disabled" : "secondary"}
                text={isSending ? "Sending..." : "Send All Messages"}
                onClick={handleSendMessagesClick}
                disabled={isSending}
                className="w-full rounded-lg"
              />
            </div>
          </div>
        </ModalWrapper>
      )}
      <ReviewResponseModal
        response={response}
        isOpen={isResponseOpen}
        setIsOpen={setIsResponseOpen}
        onClose={handleClose}
      />
    </>
  );
};

export default DispatchModal;
